import { useState, useEffect } from 'react';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { EmailDefault, EmailTemplate } from '../../../../clients/quipli-service/email/email';
import { toast } from '../../../../util/toasts';

export const useTemplateData = (emailTemplateId: string) => {
  const { getReplacementVariables, getEmailTemplates, getTemplateDefault, updateEmailTemplate } = useAPI();
  const [templateData, setTemplateData] = useState<EmailTemplate | undefined>();
  const [defaultTemplate, setDefaultTemplate] = useState<EmailDefault | undefined>();
  const [replacementVariables, setReplacementVariables] = useState<Record<string, string>>({});
  const [templateCategory, setTemplateCategory] = useState('');
  const [emailBodyValue, setEmailBodyValue] = useState('');
  const [subjectValue, setSubjectValue] = useState('');
  const [sendToAssignedRepresentative, setSendToRepresentative] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const getData = async () => {
    try {
      const emailTemplates = await getEmailTemplates();
      const templateData = emailTemplates.find((template) => template.name === emailTemplateId);
      setTemplateData(templateData);

      let replacementVariablesCall = {};
      if (templateData) {
        replacementVariablesCall = await getReplacementVariables(templateData);
        setReplacementVariables(replacementVariablesCall);
      }

      if (templateData) {
        const templateDefault = await getTemplateDefault(templateData);

        setDefaultTemplate(templateDefault);
        setTemplateCategory(templateData.categoryName);
        setEmailBodyValue(templateData.body ?? '');
        setSubjectValue(templateData.subject);
        setSendToRepresentative(templateData.sendToAssignedRepresentative);
      }
    } catch (e) {
      toast('Failed to load template data', 'error');
    }

    setIsLoading(false);
  };

  const saveTemplateData = async () => {
    if (templateData) {
      setIsSaving(true);
      try {
        await updateEmailTemplate({
          ...templateData,
          body: emailBodyValue,
          subject: subjectValue,
          sendToAssignedRepresentative,
        });
        await getData();
        toast('Template changes saved', 'success');
      } catch (e) {
        toast('Failed to save template changes', 'error');
      }
      setIsSaving(false);
    }
  };

  const restoreDefault = async () => {
    if (templateData && defaultTemplate) {
      setIsSaving(true);
      try {
        await updateEmailTemplate({
          ...templateData,
          body: defaultTemplate.body,
          subject: defaultTemplate.subject,
          sendToAssignedRepresentative: false,
        });
        await getData();
        toast('Template restored to default', 'success');
      } catch (e) {
        toast('Failed to restore default template', 'error');
      }

      setIsSaving(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getData().then(() => setIsLoading(false));
  }, [emailTemplateId]);

  return {
    templateData,
    replacementVariables,
    templateCategory,
    emailBodyValue,
    setEmailBodyValue,
    subjectValue,
    setSubjectValue,
    sendToAssignedRepresentative,
    setSendToRepresentative,
    saveTemplateData,
    isLoading,
    isSaving,
    restoreDefault,
  };
};
